
import { Device } from '../gis-map/types';
import { getEnergyDevices, getGasDevices, getWaterDevices } from '../gis-map/sampleData';

/**
 * Maps a GIS device to a smart meter format
 */
export const mapGisDeviceToSmartMeter = (device: Device) => {
  const now = new Date();
  const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  
  // Only proceed with mapping if this is actually a smart meter
  if (device.type !== 'smartMeter') {
    return null;
  }
  
  return {
    deviceId: device.id,
    location: device.location.address,
    status: device.status === 'Healthy' ? 'Healthy' : 
            device.status === 'Warning' ? 'Warning' : 'Critical',
    batteryLevel: Math.floor(Math.random() * 100),
    signalStrength: Math.floor(Math.random() * 5) + 1,
    lastCommunication: device.lastActive || now.toISOString(),
    firmwareVersion: `v${Math.floor(Math.random() * 3) + 1}.${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 10)}`,
    readings: {
      voltage: Math.floor(Math.random() * 50) + 200, // 200-250V
      current: Math.floor(Math.random() * 20) + 1, // 1-20A
      frequency: Math.floor(Math.random() * 3) + 49, // 49-51Hz
      power: Math.floor(Math.random() * 5000) + 1000, // 1000-6000W
      totalEnergy: Math.floor(Math.random() * 10000) + 5000, // 5000-15000kWh
    },
    maintenanceStatus: {
      lastMaintenance: lastMonth.toISOString(),
      nextScheduled: new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString(),
      alerts: device.status !== 'Healthy' ? ['Check required'] : []
    }
  };
};

/**
 * Get all energy smart meters from GIS including mapped ones
 */
export const getAllEnergySmartMeters = (existingMeters: any[]) => {
  // Get existing IDs to avoid duplicates
  const existingIds = new Set(existingMeters.map(meter => meter.deviceId));
  
  // Get all energy devices from GIS
  const allEnergyDevices = getEnergyDevices();
  
  // Filter for smart meters that aren't already in the list
  const newSmartMeters = allEnergyDevices
    .filter(device => device.type === 'smartMeter' && !existingIds.has(device.id))
    .map(device => mapGisDeviceToSmartMeter(device))
    .filter(Boolean); // Remove null values
    
  // Combine with existing meters
  return [...existingMeters, ...newSmartMeters];
};

/**
 * Get all gas smart meters from GIS including mapped ones
 */
export const getAllGasSmartMeters = (existingMeters: any[]) => {
  // Get existing IDs to avoid duplicates
  const existingIds = new Set(existingMeters.map(meter => meter.deviceId));
  
  // Get all gas devices from GIS
  const allGasDevices = getGasDevices();
  
  // Filter for smart meters that aren't already in the list
  const newSmartMeters = allGasDevices
    .filter(device => device.type === 'smartMeter' && !existingIds.has(device.id))
    .map(device => mapGisDeviceToSmartMeter(device))
    .filter(Boolean); // Remove null values
    
  // Combine with existing meters
  return [...existingMeters, ...newSmartMeters];
};

/**
 * Get all water smart meters from GIS including mapped ones
 */
export const getAllWaterSmartMeters = (existingMeters: any[]) => {
  // Get existing IDs to avoid duplicates
  const existingIds = new Set(existingMeters.map(meter => meter.deviceId));
  
  // Get all water devices from GIS
  const allWaterDevices = getWaterDevices();
  
  // Filter for smart meters that aren't already in the list
  const newSmartMeters = allWaterDevices
    .filter(device => device.type === 'smartMeter' && !existingIds.has(device.id))
    .map(device => mapGisDeviceToSmartMeter(device))
    .filter(Boolean); // Remove null values
    
  // Combine with existing meters
  return [...existingMeters, ...newSmartMeters];
};
